/**
 * Plan narrative subpage — /planning/plans/:cycleId/narrative/* (DEP-026).
 *
 * The unified Plan of Record surface: the cycle's linked narrative record
 * (por_records.plan_cycle_id) rendered as a plan section, reusing the whole
 * assembled instrument (PorWorkspace) under a record-scoped nav base so every
 * face — dashboard, sections, synopsis, sponsor brief, official record —
 * lives at this one route. Gated by the plan_narrative module; off → back to
 * the plan overview. Cycle-less org-level records are untouched by this page —
 * they keep the standalone instrument.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { createPor, getPorForCycle } from '../../lib/planOfRecordService';
import type { PorRecord } from '../../types/plan-of-record';
import { getPlanSnapshot } from '../../lib/planningService';
import { PlanInstrumentNavProvider, usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';
import { planNarrativeBasePath } from '../../lib/prioritizationPlanInstrumentPath';
import { PorCard, PorEmptyState, PrimaryButton } from '../../components/plan-of-record/porUi';
import PorWorkspace from '../plan-of-record/PorWorkspace';

export default function PlanNarrativePage() {
  const { cycleId } = useParams<{ cycleId: string }>();
  const { basePath } = usePlanInstrumentNav();
  const [record, setRecord] = useState<PorRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!cycleId) return;
    const { data, error: err } = await getPorForCycle(cycleId);
    if (err) setError(err);
    setRecord(data);
    setLoading(false);
  }, [cycleId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!cycleId) return;
    setCreating(true);
    setError(null);
    const { data: snapshot } = await getPlanSnapshot(cycleId);
    const cycleName = snapshot?.name ?? null;
    const { data, error: err } = await createPor({
      title: cycleName ? `${cycleName} — Narrative` : 'Plan narrative',
      plan_cycle_id: cycleId,
    });
    setCreating(false);
    if (err || !data) {
      setError(err ?? 'Failed to create the narrative record.');
      return;
    }
    await load();
  };

  if (!cycleId) return <Navigate to="/planning/plans" replace />;

  if (loading) {
    return (
      <PorCard>
        <p className="text-sm text-[var(--text-ter)]">Loading narrative record…</p>
      </PorCard>
    );
  }

  if (!record) {
    return (
      <PorCard>
        <PorEmptyState
          icon={<BookOpen className="h-7 w-7" />}
          title="No narrative record on this plan yet"
          prompt="The narrative record carries the written story behind the plan — vision, drivers, KPIs, proof points, risks, and executive asks — and is frozen with the plan's POR authorization. Create it to give this plan its narrative layer."
          action={
            <PrimaryButton onClick={() => void handleCreate()} disabled={creating}>
              {creating ? 'Creating…' : 'Create narrative record'}
            </PrimaryButton>
          }
        />
        {error && <p className="mt-3 text-xs text-luminous-coral-700">{error}</p>}
      </PorCard>
    );
  }

  return (
    <PlanInstrumentNavProvider basePath={basePath} porBasePath={planNarrativeBasePath(cycleId)}>
      <PorWorkspace porId={record.id} />
    </PlanInstrumentNavProvider>
  );
}
